import { autoserialize, autoserializeAs } from 'cerialize';

export class Contact {
  @autoserialize name: string;
  @autoserialize email: string;
  @autoserialize phone: string;
  @autoserializeAs('skip_sms') skipSms?: boolean | null;
  @autoserializeAs('skip_email') skipEmail?: boolean | null;

  constructor(contactPayload?: IContact) {
    this.name = contactPayload?.name || '';
    this.email = contactPayload?.email || '';
    this.phone = contactPayload?.phone || '';
    // Left undefined when absent rather than defaulted to false: the API omits the keys
    // entirely on every pre-existing contact, and only a literal `true` silences anything.
    this.skipSms = contactPayload?.skipSms ?? undefined;
    this.skipEmail = contactPayload?.skipEmail ?? undefined;
  }

  get formattedPhoneValue(): string {
    let val: string = this.phone;
    if (val?.length > 0) {
      val = `(${val.slice(0, 3)}) ${val.slice(3, 6)} ${val.slice(6, 10)}`;
    }
    return val;
  }
}

export interface IContact {
  name: string;
  email?: string;
  phone?: string;
  skipSms?: boolean | null;
  skipEmail?: boolean | null;
}

/**
 * RP-4071: whether automated SMS/email to this contact is silenced. The API carries one
 * flag per channel so the two can be split later; product asked for a single control, so
 * the client drives both together and reads the pair as "silenced if either is set".
 */
export function isContactSilenced(contact?: Partial<IContact> | null): boolean {
  return !!contact?.skipSms || !!contact?.skipEmail;
}

/**
 * The patch one toggle applies. `null` for off, never `false`: an all-empty contact is
 * dropped before it is sent (UtilityService.removeNestedObjectWithAllEmptyValues keeps
 * any object holding a non-null value), and a `false` would turn "no contact" into a
 * blank contact object — which attr_json saves over the real one, unvalidated.
 */
export function contactSilencePatch(silenced: boolean): Pick<IContact, 'skipSms' | 'skipEmail'> {
  return silenced ? { skipSms: true, skipEmail: true } : { skipSms: null, skipEmail: null };
}

/**
 * Turn a contact form value carrying the single `silenced` control into the payload the API
 * expects: the contact plus the flag pair, with the control key dropped.
 *
 * The flags ride along ONLY with a contact that holds something. They are non-null, so on
 * an empty contact they would stop UtilityService.removeNestedObjectWithAllEmptyValues from
 * dropping the object, and attr_json writes a brand-new blank Contact over whatever is
 * stored — silently, since Embedded::Contact's name validation is not propagated.
 */
export function contactWithSilence<T extends Partial<IContact> & { silenced?: boolean | null }>(contact: T): Omit<T, 'silenced'> {
  const { silenced, ...rest } = contact;
  const hasValue = !!(rest.name?.trim() || rest.email?.trim() || rest.phone?.trim());
  return hasValue ? { ...rest, ...contactSilencePatch(!!silenced) } : rest;
}

/**
 * RP-4126: the fields a courier contact must carry — a NAME and a PHONE. Email is
 * optional, confirmed with the API team (2026-09-02): dispatch takes only name and phone
 * (`Donations::AssignCourier` → the courier's `name`/`phone` and `dropoff_phone_number`),
 * so an email address is nothing a courier can act on.
 *
 * Exported as ONE rule because two places must apply exactly it: the store form's
 * validators (which courier fields are required while "Accepts Courier" is ticked) and
 * the charity courier tab's ✓/✗ + toggle gate. When they disagreed, the tab could pass a
 * store the form refused to save. RP-4072 redefines what "valid" means — here.
 */
export const COURIER_CONTACT_REQUIRED_FIELDS = ['name', 'phone'] as const satisfies readonly (keyof IContact)[];

/** Whether a courier contact is complete enough to dispatch against. */
export function isCourierContactValid(contact?: Partial<IContact> | null): boolean {
  if (!contact) return false;
  return COURIER_CONTACT_REQUIRED_FIELDS.every((field) => !!contact[field]?.trim());
}
